import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileInput", "selected", "fileArea", "progressTrack", "filePreview",
    "uploadingTitle", "uploadingFileName", "uploadingState",
    "defaultIcon", "defaultTitle", "defaultHint",
    "columnsValue", "rowsValue", "ignoredValue", "ignoredInfo",
    "domainColumnSelect", "domainExamples", "domainColumnSection"]

  static values = { types: Array, size: Number, maxRows: { type: Number, default: 100000 } }

  // Added instance variables to store column data
  columnNames = []
  sampleData = {}
  columnsWithDomains = {}

  // This is to capture drag events anywhere on the page
  connect() {
    document.body.addEventListener("dragover", this.handleDragOver.bind(this))
    document.body.addEventListener("dragleave", this.handleDragLeave.bind(this))
    document.body.addEventListener("drop", this.handleDrop.bind(this))

    // Dispatch event on initial load to disable button
    this.dispatchFileEvent(false)
  }

  disconnect() {
    document.body.removeEventListener("dragover", this.handleDragOver.bind(this))
    document.body.removeEventListener("dragleave", this.handleDragLeave.bind(this))
    document.body.removeEventListener("drop", this.handleDrop.bind(this))
  }

  // Dispatch custom event for file selection status
  dispatchFileEvent(isFileSelected) {
    const event = new CustomEvent('file-upload:status-changed', {
      bubbles: true,
      detail: {
        isFileSelected: isFileSelected
      }
    });
    this.element.dispatchEvent(event);
  }

  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropFile(e)
  }

  dropFile(e) {
    this.fileInputTarget.files = e.dataTransfer.files

    // We manually trigger the "change" event as else it's not triggered
    // by the browser and we don't enter the "selectFile" function.
    this.fileInputTarget.dispatchEvent(new Event("change"))
  }

  openFileSelection() {
    this.fileInputTarget.click()
  }

  selectFile(e) {
    const acceptedExtensions = this.typesValue
    const maxSize = this.sizeValue
    const file = e.target.files[0]

    if (!file) return

    const nameSplitted = file.name.split(".")
    const extension = nameSplitted[nameSplitted.length - 1].toLowerCase()

    if (acceptedExtensions.indexOf(extension) === -1) {
      let message
      if (acceptedExtensions.length === 1) {
        message = i18n.t("js.back.file_upload_enhanced.invalid_extension_single", { extension: acceptedExtensions[0] })
      } else {
        message = i18n.t("js.back.file_upload_enhanced.invalid_extension_multiple", { extensions: acceptedExtensions.join(", ") })
      }

      this.fileInputTarget.value = ""
      this.displayFlash("danger", message)
      this.dispatchFileEvent(false)
    } else if (file.size > maxSize) {
      const message = i18n.t("js.back.file_upload_enhanced.file_too_large", { size: (maxSize / 1000000).toFixed(0) })

      this.fileInputTarget.value = ""
      this.displayFlash("danger", message)
      this.dispatchFileEvent(false)
    } else {
      // Show uploading state
      this.showUploadingState(file)

      // Begin file parsing
      this.parseFile(file)
    }
  }

  displayFlash(type, message) {
    App.displayFlash(type, message)
  }

  showUploadingState(file) {
    this.fileAreaTarget.classList.add('is-uploading')

    if (this.hasDefaultIconTarget) {
      this.defaultIconTarget.classList.add('d-none')
    }

    if (this.hasDefaultTitleTarget) {
      this.defaultTitleTarget.classList.add('d-none')
    }

    if (this.hasDefaultHintTarget) {
      this.defaultHintTarget.classList.add('d-none')
    }

    if (this.hasUploadingStateTarget) {
      this.uploadingStateTarget.classList.remove('d-none')
    }

    if (this.hasUploadingFileNameTarget) {
      this.uploadingFileNameTarget.textContent = file.name
    }

    if (this.hasProgressTrackTarget) {
      this.progressTrackTarget.style.width = "0%"
      this.progressTrackTarget.setAttribute("aria-valuenow", "0")
    }
  }

  parseFile(file) {
    const reader = new FileReader()

    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        // Calculate and update progress percentage (first 50% is reading file)
        const progressPercentage = Math.round((event.loaded / event.total) * 100 / 2)
        this.updateProgress(progressPercentage)
      }
    }

    reader.onload = (event) => {
      const content = event.target.result
      this.processFileContent(content, file)
    }

    reader.onerror = () => {
      this.fileInputTarget.value = ""
      this.displayFlash("danger", i18n.t("js.back.file_upload_enhanced.read_error"))
      this.resetUploadState()
      this.dispatchFileEvent(false)
    }

    // Start reading the file
    reader.readAsText(file)
  }

  // Enhanced CSV line parser that properly handles quoted fields with newlines
  parseCSVProperly(text, delimiter = ',') {
    // If empty input, return empty result
    if (!text || !text.trim()) return [];

    let rows = [];
    let currentRow = [];
    let currentField = "";
    let inQuotes = false;

    // Process each character
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = i < text.length - 1 ? text[i + 1] : null;

      // Handle quotes
      if (char === '"') {
        // Check for escaped quotes (double quotes)
        if (inQuotes && nextChar === '"') {
          currentField += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      }
      // Handle delimiters
      else if (char === delimiter && !inQuotes) {
        currentRow.push(currentField);
        currentField = "";
      }
      // Handle newlines
      else if ((char === '\n' || (char === '\r' && nextChar === '\n')) && !inQuotes) {
        currentRow.push(currentField);
        rows.push(currentRow);
        currentRow = [];
        currentField = "";

        // Skip the \n if we just processed \r
        if (char === '\r' && nextChar === '\n') {
          i++;
        }
      }
      // All other characters
      else {
        currentField += char;
      }
    }

    // Add the last field and row if not empty
    if (currentField || currentRow.length > 0) {
      currentRow.push(currentField);
      rows.push(currentRow);
    }

    return rows;
  }

  processFileContent(content, file) {
    // Start at 50% progress since file reading is complete
    let progress = 50
    this.updateProgress(progress)

    try {
      const rows = this.parseCSVProperly(content);

      if (rows.length === 0) {
        throw new Error(i18n.t("js.back.file_upload_enhanced.empty_file"))
      }

      // Get headers - first row
      const headers = rows[0].map(header => header.trim()).filter(header => header.length > 0);

      // Store column names
      this.columnNames = headers;

      // Get sample data
      const sampleData = {};
      headers.forEach(header => {
        sampleData[header] = [];
      });

      // Get up to 5 rows of data
      const dataRowsLimit = Math.min(5, rows.length - 1);
      for (let i = 1; i <= dataRowsLimit; i++) {
        if (i < rows.length) {
          const row = rows[i];
          for (let j = 0; j < headers.length && j < row.length; j++) {
            const value = row[j].trim();
            if (value) {
              sampleData[headers[j]].push(value);
            }
          }
        }
      }

      this.sampleData = sampleData;

      const likelyDomainColumn = this.detectDomainColumn(headers, sampleData);

      const dataRowCount = rows.length - 1;

      const maxRows = this.maxRowsValue;
      const ignoredRows = dataRowCount > maxRows ? dataRowCount - maxRows : 0;

      // Store file data
      this.fileData = {
        columns: headers.length,
        rows: dataRowCount,
        ignored: ignoredRows,
        columnNames: headers,
        sampleData: sampleData,
        likelyDomainColumn: likelyDomainColumn
      };

      // Gradually increase progress to 100%
      const progressInterval = setInterval(() => {
        progress += 5
        if (progress >= 100) {
          clearInterval(progressInterval)

          // Show file preview with the file data
          this.showFilePreviewWithData(file, this.fileData)
        }
        this.updateProgress(progress)
      }, 50)

    } catch (error) {
      this.fileInputTarget.value = ""
      this.displayFlash("danger", i18n.t("js.back.file_upload_enhanced.processing_error", { error: error.message }))
      this.resetUploadState()
      this.dispatchFileEvent(false)
    }
  }

  // Detect which column likely contains domains
  detectDomainColumn(columnNames, sampleData) {
    // Domain patterns to check for
    const domainPatterns = [
      /^(https?:\/\/)?([a-zA-Z0-9]+[a-zA-Z0-9-]*\.)+([a-zA-Z]{2,})(\/.*)?/,
    ]

    // Column name patterns that might indicate a domain column
    const namePatterns = [
      /domain/i,
      /website/i,
      /url/i,
      /site/i,
      /web/i
    ]

    // Score each column based on likely domain content
    const scores = {}
    // Also track which columns contain domains
    const columnsWithDomains = {}

    columnNames.forEach(column => {
      scores[column] = 0
      columnsWithDomains[column] = false

      // Score based on column name
      namePatterns.forEach(pattern => {
        if (pattern.test(column)) {
          scores[column] += 5
        }
      })

      // Score based on sample data content
      if (sampleData[column] && sampleData[column].length > 0) {
        sampleData[column].forEach(sample => {
          const cleanSample = (sample || '').trim()
          if (cleanSample) {
            domainPatterns.forEach(pattern => {
              if (pattern.test(cleanSample)) {
                scores[column] += 3
                columnsWithDomains[column] = true
              }
            })
          }
        })
      }
    })

    // Find column with highest score
    let bestMatch = null
    let highestScore = -1

    for (const column in scores) {
      if (scores[column] > highestScore) {
        highestScore = scores[column]
        bestMatch = column
      }
    }

    // Store which columns contain domains for the selector
    this.columnsWithDomains = columnsWithDomains

    // Default to first column if no good match
    return bestMatch || columnNames[0]
  }

  updateProgress(percentage) {
    if (this.hasProgressTrackTarget) {
      this.progressTrackTarget.style.width = `${percentage}%`
      this.progressTrackTarget.setAttribute("aria-valuenow", percentage.toString())
    }
  }

  showFilePreviewWithData(file, data) {
    // Hide file area with uploading state
    this.fileAreaTarget.classList.remove('is-uploading')
    this.fileAreaTarget.classList.add('d-none')

    // Show file preview
    if (this.hasFilePreviewTarget) {
      // Update file name and details
      this.selectedTarget.textContent = file.name

      // Format the file size in KB or MB
      const fileSize = file.size < 1000000
        ? `${(file.size / 1024).toFixed(1)} kB`
        : `${(file.size / 1048576).toFixed(1)} MB`

      // Update file meta information
      const fileMetaElement = this.selectedTarget.nextElementSibling
      if (fileMetaElement) {
        fileMetaElement.textContent = i18n.t("js.back.file_upload_enhanced.file_meta", {
          ext: file.name.split('.').pop().toUpperCase(),
          size: fileSize
        })
      }

      // Update count values with actual data
      if (this.hasColumnsValueTarget) {
        this.columnsValueTarget.textContent = data.columns
      }

      if (this.hasRowsValueTarget) {
        this.rowsValueTarget.textContent = this.formatNumber(data.rows)
      }

      // Only show ignored rows if there are any
      if (this.hasIgnoredValueTarget) {
        this.ignoredValueTarget.textContent = this.formatNumber(data.ignored)

        // Hide or show the ignored info based on whether there are ignored rows
        if (this.hasIgnoredInfoTarget) {
          if (data.ignored > 0) {
            this.ignoredInfoTarget.classList.remove('d-none')
          } else {
            this.ignoredInfoTarget.classList.add('d-none')
          }
        }
      }

      // Show the file preview section
      this.filePreviewTarget.classList.remove('d-none')

      // Populate and show domain column selector
      if (this.hasDomainColumnSelectTarget && this.hasDomainColumnSectionTarget) {
        this.populateDomainColumnSelector(
          data.columnNames,
          data.likelyDomainColumn,
          data.sampleData
        )
      }

      // Dispatch event that file is valid and processed
      this.dispatchFileEvent(true)
    }
  }

  // Populate domain column selector and show it
  populateDomainColumnSelector(columnNames, likelyDomainColumn, sampleData) {
    // Clear existing options
    this.domainColumnSelectTarget.innerHTML = ""

    // Add options for each column
    columnNames.forEach((column, index) => {
      const option = document.createElement("option")
      option.value = index.toString()
      option.textContent = column

      // If column doesn't contain domains, disable it and add explanation
      if (!this.columnsWithDomains[column]) {
        option.disabled = true
        option.textContent = `${column} (this column doesn't contain URLs)`
      } else {
        option.textContent = column
      }

      // Select the likely domain column
      if (column === likelyDomainColumn) {
        option.selected = true
      }

      this.domainColumnSelectTarget.appendChild(option)
    })

    // Update examples for selected column
    this.updateDomainExamples(likelyDomainColumn, sampleData)

    // Show the domain column selector section
    this.domainColumnSectionTarget.classList.remove("d-none")
  }

  // Update examples when domain column changes
  updateDomainExamples(columnName, sampleData) {
    if (this.hasDomainExamplesTarget && sampleData[columnName]) {
      // Get up to 3 examples from sample data
      const examples = sampleData[columnName].slice(0, 3).map(url => {
        // Extract just the domain with www
        return this.extractDomain(url)
      }).join(" · ")

      // Using i18n.t() for internationalization
      this.domainExamplesTarget.textContent = i18n.t("js.back.file_upload_enhanced.domain_examples", { examples: examples })
    }
  }

  // Extract just the domain from a URL
  extractDomain(url) {
    // If it's not a string or is empty, return original string
    if (!url || typeof url !== 'string') return url || '';

    try {
      // Try to parse as URL if it has protocol
      let domain = url.trim();

      // Add protocol if missing to make URL parsing work
      if (!domain.match(/^https?:\/\//i)) {
        domain = 'http://' + domain;
      }

      try {
        const parsedUrl = new URL(domain);

        let hostname = parsedUrl.hostname;

        if (!hostname.startsWith('www.') && !hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
          hostname = 'www.' + hostname;
        }

        // If hostname is empty, return the original URL
        return hostname || url;
      } catch {
        // If URL parsing fails, do basic extraction
        const domainMatch = domain.match(/(?:https?:\/\/)?(?:www\.)?([^/]+)/i);
        // If match is found, return with www prefix, otherwise return original URL
        return domainMatch ? 'www.' + domainMatch[1] : url;
      }
    } catch {
      return url;
    }
  }

  // Handler for domain column selection change
  domainColumnChanged(event) {
    const columnIndex = parseInt(event.target.value, 10)

    const columnName = this.columnNames[columnIndex]

    // Only update if we have valid data
    if (columnName && this.sampleData[columnName]) {
      this.updateDomainExamples(columnName, this.sampleData)
    }
  }

  formatNumber(number) {
    // Format number with thousands separators
    return new Intl.NumberFormat().format(number)
  }

  resetUploadState() {
    // Reset to default upload state
    this.fileAreaTarget.classList.remove('is-uploading')

    // Show default elements
    if (this.hasDefaultIconTarget) {
      this.defaultIconTarget.classList.remove('d-none')
    }

    if (this.hasDefaultTitleTarget) {
      this.defaultTitleTarget.classList.remove('d-none')
    }

    if (this.hasDefaultHintTarget) {
      this.defaultHintTarget.classList.remove('d-none')
    }

    // Hide uploading state
    if (this.hasUploadingStateTarget) {
      this.uploadingStateTarget.classList.add('d-none')
    }
  }

  closeSelected(e) {
    // Prevent normal button action if this is triggered by a button
    if (e) e.preventDefault()

    // Reset file input
    this.fileInputTarget.value = ""

    // Hide file preview
    if (this.hasFilePreviewTarget) {
      this.filePreviewTarget.classList.add('d-none')
    }

    // Reset file area
    this.fileAreaTarget.classList.remove('d-none')
    this.fileAreaTarget.classList.remove('is-uploading')

    // Show default elements
    if (this.hasDefaultIconTarget) {
      this.defaultIconTarget.classList.remove('d-none')
    }

    if (this.hasDefaultTitleTarget) {
      this.defaultTitleTarget.classList.remove('d-none')
    }

    if (this.hasDefaultHintTarget) {
      this.defaultHintTarget.classList.remove('d-none')
    }

    // Hide uploading state
    if (this.hasUploadingStateTarget) {
      this.uploadingStateTarget.classList.add('d-none')
    }

    // Also hide the domain column section
    if (this.hasDomainColumnSectionTarget) {
      this.domainColumnSectionTarget.classList.add('d-none')
    }

    // Dispatch event that file is removed
    this.dispatchFileEvent(false)
  }
}
